import React, { useState } from 'react';
import { Collapse } from '@mui/material';
import { ScraperCategory } from '../../../types/scrapedServicesTypes';
import { Icon } from '@iconify/react';
import CheckboxInput from '../../../../../muiComponents/CheckboxInput';
import { scraperServiceStatusConfig } from '../../../utils/AIScraperPanelsUtils';
import ServiceAttributeRow from './ServiceAttributeRow';
import ScraperStatusTag from './ScraperStatusTag';
import IconButton from '../../../../../muiComponents/IconButton';
import { commonIcons } from '../../../../../utils/commonVariables/commonIcons';
import { useServiceScraperPanel } from '../../../contexts/ServiceScraperPanelContext';

interface ServiceCategoryScraperSectionProps {
    category: ScraperCategory;
}

/**
 * Renders a scraper category with collapsible services.
 * @param props - The category to review.
 * @returns The category header and its expandable service list.
 */
const ServiceCategoryScraperSection = ({
    category,
}: ServiceCategoryScraperSectionProps) => {
    const [isExpanded, setIsExpanded] = useState(true);
    const { setServiceChecked } = useServiceScraperPanel();

    return (
        <div
            key={category.IdServiceCategory}
            className='service-scraper__category-section'
        >
            <div className='service-scraper__category-header'>
                <div className='flex flex-align-center gap-10'>
                    <div className='service-scraper__category-icon'>
                        <Icon icon={category.Icon} />
                    </div>
                    <span>{category.ServiceCategory}</span>
                </div>
                <IconButton
                    type='button'
                    className='service-scraper__category-toggle'
                    aria-label={category.ServiceCategory}
                    aria-expanded={isExpanded}
                    onClick={() => setIsExpanded(previous => !previous)}
                    iconName={
                        isExpanded
                            ? commonIcons.ARROW_UP
                            : commonIcons.ARROW_DOWN
                    }
                />
            </div>
            <Collapse in={isExpanded} timeout='auto'>
                <ul className='service-scraper__service-list'>
                    {category.Services.map(service => (
                        <li
                            key={service.IdServiceType}
                            className='service-scraper__service-type'
                        >
                            <div className='service-scraper__service-type__header'>
                                <CheckboxInput
                                    label={service.ServiceType}
                                    checked={service.Checked}
                                    onChange={(_, checked) =>
                                        setServiceChecked(
                                            category.IdServiceCategory,
                                            service.IdServiceType,
                                            checked
                                        )
                                    }
                                />
                                <ScraperStatusTag
                                    text={service.Status}
                                    color={
                                        scraperServiceStatusConfig[
                                            service.Status
                                        ]
                                    }
                                />
                            </div>
                            {service.AttributeDefinitions.length ? (
                                <ul className='service-scraper__attribute-list'>
                                    {service.AttributeDefinitions.map(
                                        attribute => (
                                            <ServiceAttributeRow
                                                key={
                                                    attribute.IdAttributeDefinition
                                                }
                                                attribute={attribute}
                                                categoryId={
                                                    category.IdServiceCategory
                                                }
                                                serviceTypeId={
                                                    service.IdServiceType
                                                }
                                                disabled={!service.Checked}
                                            />
                                        )
                                    )}
                                </ul>
                            ) : null}
                        </li>
                    ))}
                </ul>
            </Collapse>
        </div>
    );
};

export default ServiceCategoryScraperSection;
